/**
 * mentionAffordance — the one-keystroke @mention helper for gated threads
 * (owner directive 2026-08-11; the GROUP form Miles asked for on 2026-09-20).
 *
 * In a group thread that requires an @Prest0n mention, addressing anyone is a
 * typing chore the product itself created. This module decides, purely from
 * thread state, WHO a single Tab press (or one click on the composer chip)
 * should mention:
 *
 *   • While a REPLY is in progress: everyone that reply addresses and the
 *     draft does not yet carry — its author first, then the people the
 *     message mentioned (replyAllMentions.ts, the one rule the reply seed
 *     reads too). Nothing else: the person is answering THAT message, so the
 *     chip never offers whoever happened to write last.
 *   • Prest0n, when it sent the latest message — the user is most plausibly
 *     continuing with Prest0n and the gate would otherwise silence them; also
 *     the default when the thread is empty or the last sender is the user
 *     themself (they are most plausibly summoning Prest0n next).
 *   • The HUMAN who sent the latest message otherwise, TOGETHER WITH whoever
 *     that message addressed (Miles, 2026-09-20: "the last individual that
 *     wrote on the channel or the group of individuals writing / mentioned
 *     in the last mention") — answering the people in the conversation's
 *     last exchange is the natural next act in a group conversation.
 *
 * Returns null whenever the affordance would be noise: the thread doesn't
 * gate, the requirement is relaxed, or the draft already carries every
 * target's mention. Pure and dependency-light (predicates + handle
 * derivation only) so the rule unit-tests without React.
 */

import { mentionsAssistant } from './summonGate';
import { ASSISTANT_MENTION } from './suggestionMention';
import { replyAllMentions } from './replyAllMentions';
import type { ReplyAllMention, ReplyAllTarget } from './replyAllMentions';
import type { MentionRosterEntry } from './mentionResolver';

/** Sentinel senderId the shared store uses for assistant turns. */
const ASSISTANT_SENDER_ID = 'assistant';

/** A chip is a chip: at most this many people ride one press (the reply seed's own ceiling is wider). */
export const MENTION_AFFORDANCE_MAX_TARGETS = 3;

export interface MentionAffordanceMessage {
  isUser?: boolean;
  isSystem?: boolean;
  senderId?: string | null;
  /** The message's words — who it addressed (absent ⇒ nobody beyond its author). */
  text?: string | null;
}

export interface MentionAffordanceTarget {
  /** Plain handle to insert after '@' (no '@' included), e.g. 'Prest0n'. */
  handle: string;
  /** Display label for the chip, e.g. '@Prest0n'. */
  display: string;
  kind: 'assistant' | 'member' | 'team';
}

export interface MentionAffordanceGroup {
  /** In insertion order; never empty (null is returned instead of an empty group). */
  targets: MentionAffordanceTarget[];
  /** The chip's label and what one press inserts: the displays joined by a space, e.g. `@stephen @justinz`. */
  display: string;
}

export interface MentionAffordanceArgs {
  /** The active thread actually gates (summon flag on + ≥2 humans). */
  gatesThisThread: boolean;
  /** The thread's effective mention requirement. */
  requireMention: boolean | null | undefined;
  /** Current composer draft (plain text). */
  draftText: string | null | undefined;
  /** Thread messages, oldest first (only the tail is inspected). */
  messages: ReadonlyArray<MentionAffordanceMessage> | undefined;
  /** The current user's uid. */
  selfId?: string | null;
  /** Account roster for handle derivation (absent ⇒ assistant fallback). */
  roster?: ReadonlyArray<MentionRosterEntry>;
  /** The reply in progress, when there is one: the chip then completes ITS addressees and nothing else. */
  replyTarget?: ReplyAllTarget | null;
  /** The thread's participants when known — a person outside the thread is never offered. */
  participantIds?: ReadonlySet<string> | ReadonlyArray<string> | null;
}

const ASSISTANT_TARGET: MentionAffordanceTarget = {
  handle: ASSISTANT_MENTION.slice(1),
  display: ASSISTANT_MENTION,
  kind: 'assistant',
};
const ASSISTANT_GROUP: MentionAffordanceGroup = { targets: [ASSISTANT_TARGET], display: ASSISTANT_TARGET.display };

/** The latest non-system message, or null. */
function lastRealMessage(
  messages: ReadonlyArray<MentionAffordanceMessage> | undefined,
): MentionAffordanceMessage | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.isSystem !== true) return m;
  }
  return null;
}

function toGroup(mentions: ReplyAllMention[]): MentionAffordanceGroup | null {
  if (mentions.length === 0) return null;
  const targets = mentions.map((m): MentionAffordanceTarget => ({ handle: m.handle, display: `@${m.handle}`, kind: m.kind }));
  return { targets, display: targets.map((t) => t.display).join(' ') };
}

export function resolveMentionAffordanceGroup(args: MentionAffordanceArgs): MentionAffordanceGroup | null {
  if (!args.gatesThisThread || args.requireMention === false) return null;

  const draft = typeof args.draftText === 'string' ? args.draftText : '';
  const roster = args.roster || [];
  const self = (args.selfId ?? '').trim();
  const shared = {
    roster,
    selfId: self,
    gates: { gatesThisThread: true, requireMention: args.requireMention },
    draftText: draft,
    participantIds: args.participantIds ?? null,
    limit: MENTION_AFFORDANCE_MAX_TARGETS,
  };

  // A reply in progress: complete ITS addressees — and nothing else. Once the
  // draft carries them all there is nothing left to offer for this reply.
  if (args.replyTarget) {
    return toGroup(replyAllMentions({ ...shared, target: args.replyTarget }).mentions);
  }

  const last = lastRealMessage(args.messages);
  const lastIsAssistant = !last || last.isUser !== true || last.senderId === ASSISTANT_SENDER_ID;
  const lastSender = (last?.senderId ?? '').trim();

  if (lastIsAssistant || !lastSender || lastSender === self) {
    // Summon Prest0n — unless the draft already does.
    return mentionsAssistant(draft) ? null : ASSISTANT_GROUP;
  }

  const member = roster.find((m) => (m.userId ?? '').trim() === lastSender);
  if (!member) {
    // Unknown sender (roster not loaded / departed member): fall back to the
    // assistant target so Tab always does something useful in a gated thread.
    return mentionsAssistant(draft) ? null : ASSISTANT_GROUP;
  }

  // A human teammate spoke last — offer them, and whoever they addressed.
  return toGroup(replyAllMentions({
    ...shared,
    target: { senderId: lastSender, isUser: true, text: last?.text ?? '' },
  }).mentions);
}

/**
 * The single-target form: the group's FIRST target (the person a reply
 * answers first, or Prest0n). Kept for callers that want one name; the chip
 * itself renders the group.
 */
export function resolveMentionAffordance(args: MentionAffordanceArgs): MentionAffordanceTarget | null {
  return resolveMentionAffordanceGroup(args)?.targets[0] ?? null;
}
